import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'react-icons-kit';
import { ic_search } from 'react-icons-kit/md/ic_search';
import { inputClasses } from 'styles/tailwindClassnames';
import { DragPathRef } from './dragAndDrop';
import TreeNode from './TreeNode';

// The Source Payload tree with its "Filter fields…" box. The filter text is
// local state here, so typing redraws only the tree, not the mappings panel.
function SourceTree({
  sampleInput,
  readOnly,
  treeRef,
  onEscape,
  collapsed,
  onToggle,
  dragPathRef,
  onLeafActivate,
  pickActive,
  armedPathKey,
  mappedTo,
}: {
  sampleInput: any;
  readOnly: boolean;
  treeRef: React.RefObject<HTMLDivElement>;
  onEscape: (e: React.KeyboardEvent) => void;
  collapsed: Set<string>;
  onToggle: (key: string) => void;
  dragPathRef: DragPathRef;
  onLeafActivate: (path: string[]) => void;
  pickActive: boolean;
  armedPathKey?: string;
  mappedTo: Record<string, string[]>;
}) {
  const { t } = useTranslation();
  const [treeFilter, setTreeFilter] = useState('');
  const normalizedFilter = treeFilter.trim().toLowerCase();
  const isEmpty = Object.keys(sampleInput).length === 0;

  // One pass over the sample: a node stays visible when its path, or any
  // descendant's path, contains the filter text.
  const visiblePaths = useMemo(() => {
    if (!normalizedFilter) return null;
    const visible = new Set<string>();
    const walk = (value: any, path: string): boolean => {
      let hit = path.toLowerCase().includes(normalizedFilter);
      if (value && typeof value === 'object') {
        Object.entries(value).forEach(([k, child]) => {
          if (walk(child, path ? `${path}.${k}` : k)) hit = true;
        });
      }
      if (hit && path) visible.add(path);
      return hit;
    };
    walk(sampleInput, '');
    return visible;
  }, [sampleInput, normalizedFilter]);

  return (
    <>
      {!isEmpty && (
        <div className="border-b border-ui-divider-lines px-3.5 py-2.5">
          <div className="relative">
            <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-greyscale-5">
              <Icon icon={ic_search} size={16} />
            </span>
            <input
              type="search"
              value={treeFilter}
              onChange={(e) => setTreeFilter(e.target.value)}
              placeholder={t('Filter fields…')}
              aria-label={t('Filter fields…')}
              data-testid="JqFilterBuilder-filter-input"
              className={`${inputClasses.standard} w-full pl-8 text-body-sm`}
            />
          </div>
        </div>
      )}
      <div
        ref={treeRef}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onEscape(e);
        }}
        className={`flex-1 overflow-y-auto py-1.5 ${pickActive ? 'rounded ring-2 ring-inset ring-ui-cta-primary' : ''}`}
      >
        {isEmpty ? (
          <div className="px-3.5 py-2 text-body-sm italic text-ui-text-primary-text">
            {!readOnly
              ? t('Use the pencil to add a sample payload')
              : t('No sample payload')}
          </div>
        ) : (
          Object.entries(sampleInput).map(([key, value]) => (
            <TreeNode
              key={key}
              name={key}
              value={value}
              path={[key]}
              depth={0}
              collapsed={collapsed}
              onToggle={onToggle}
              readOnly={readOnly}
              dragPathRef={dragPathRef}
              onLeafActivate={onLeafActivate}
              pickActive={pickActive}
              armedPathKey={armedPathKey}
              mappedTo={mappedTo}
              visiblePaths={visiblePaths}
            />
          ))
        )}
      </div>
    </>
  );
}

export default SourceTree;
